import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useGetCostDetails } from "api/queries/quotation/useGetCostDetails";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import usePermissions from "hooks/usePermissions";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import PropTypes from "prop-types";
import "./subQuote.scss";
import { FormattedMessage } from "react-intl";
import ReactHtmlParser from "react-html-parser";
import { replaceLinebreaks } from "helpers/commonHelpers";
import OverlayLoader from "components/atoms/OverlayLoader/OverlayLoader";
import testIds from "constants/testIds";
import AdditionalInfo from "./AdditionalInfo";
import CostDetailDialog from "../CostDetailDialog/CostDetailDialog";
import QuoteMeasurementSummary from "../CostDetailDialog/components/QuoteMeasurementSummary";
import "../CostDetailDialog/components/costDetails.scss";

function SubQuote({ row }) {
  const { reference, service, status, schedule } = row.original;
  const { id } = useParams();
  const shipmentId = id?.toUpperCase();

  const { permissions } = usePermissions();
  const userType = permissions && JSON.parse(permissions).user_type;
  const canViewQuotePricing =
    (permissions && JSON.parse(permissions).permissions?.booking?.view_quote_pricing) || userType === "provider";

  const [freightModeType, setfreightModeType] = useState("");

  const { data: quoteRequestData } = useGetQuoteRequestDetails({
    shipmentId,
    options: { enabled: !!shipmentId },
  });

  const stopDetailsMap = useMemo(() => {
    const map = new Map();
    if (!quoteRequestData?.organization?.good_items) return map;
    quoteRequestData.organization.good_items.forEach((item) => {
      [item.pickup_stop, item.delivery_stop].forEach((stop) => {
        if (stop && stop.stop_id && !map.has(stop.stop_id)) {
          map.set(stop.stop_id, stop);
        }
      });
    });
    return map;
  }, [quoteRequestData]);

  const quotedStops = schedule?.stops;

  const getStopTypeLabel = (stop) => {
    const baseType = stop.stop_type === "Pickup" ? "Pickup" : "Delivery";
    if (stop.stop_address_type === "Airport") return `${baseType} (Airport)`;
    if (stop.stop_address_type === "Port") return `${baseType} (Port)`;
    return baseType;
  };

  const formatRequestedTime = (stop) => {
    const formatDt = (dt) => moment(dt).format("DD MMM YYYY, HH:mm");
    switch (stop.stop_time_type) {
      case "After":
        return `Requested: After ${formatDt(stop.stop_after_datetime)}`;
      case "Before":
        return `Requested: Before ${formatDt(stop.stop_before_datetime)}`;
      case "Between":
        return `Requested: ${formatDt(stop.stop_after_datetime)} - ${formatDt(stop.stop_before_datetime)}`;
      default:
        return `Requested: ${formatDt(stop.stop_after_datetime)}`;
    }
  };

  const handleCostDetailDialog = (reference, service) => {
    setfreightModeType(service?.freight_mode_type);
  };

  useEffect(() => {
    handleCostDetailDialog(reference, service);
  }, [reference]);

  const {
    data: costDetailsData,
    isLoading: loadingCostDetails,
    isRefetching,
  } = useGetCostDetails(reference, {
    enabled: !!reference,
  });

  if (loadingCostDetails) return <LoadingSkeleton noShadow rows={1} />;

  return (
    <div
      className="d-flex flex-sm-column flex-md-row"
      style={{ backgroundColor: "#F7F9FA", borderRadius: "8px", gap: "22px", position: "relative" }}
      data-testid={testIds.SHIPMENT_DASHBOARD_QUOTE_EXPANSION}
    >
      <Col className="d-flex flex-column" style={{ padding: 0, gap: "8px" }} md={6} sm={12}>
        {quotedStops?.length > 0 && stopDetailsMap.size > 0 && (
          <div className="schedule-card">
            <div className="schedule-card-header">
              <div style={{ width: "184px" }}>
                <FormattedMessage id="quote-request.scheduleAddress" defaultMessage="Address" />
              </div>
              <div style={{ flex: 1 }}>
                <FormattedMessage id="quote-request.scheduleScheduledTime" defaultMessage="Scheduled time" />
              </div>
            </div>
            <div className="schedule-card-rows">
              {quotedStops.map((quotedStop) => {
                const stopDetails = stopDetailsMap.get(quotedStop.stop_id);
                if (!stopDetails) return null;
                const quotedDateTime = moment(`${quotedStop.quoted_date} ${quotedStop.quoted_time}`).format(
                  "DD MMM YYYY, HH:mm",
                );
                return (
                  <div key={quotedStop.stop_id} className="schedule-card-row">
                    <div className="schedule-card-address">
                      <div className="schedule-card-stop-type">{getStopTypeLabel(stopDetails)}</div>
                      <div className="schedule-card-address-name">{stopDetails.address_details?.name}</div>
                    </div>
                    <div className="schedule-card-time">
                      <div className="schedule-card-quoted-time">{quotedDateTime}</div>
                      <div className="schedule-card-requested-time">{formatRequestedTime(stopDetails)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {canViewQuotePricing && (
          <>
            <QuoteMeasurementSummary summary={costDetailsData?.quote_measurement_summary} />
            <div style={{ padding: "8px 16px", borderRadius: "8px", backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
              <CostDetailDialog costDetailsData={costDetailsData} quotationStatus={status} />
              {costDetailsData?.not_included_in_price ? (
                <section className="pt-4">
                  <Row>
                    <Col className="font-weight-bold font-b3" lg={12} md={12} sm={12}>
                      <FormattedMessage id="quote-request.costDetailNotInclude" defaultMessage="Not included" />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={12} md={12} sm={12} className="text-secondary font-b3" style={{ whiteSpace: "pre-wrap" }}>
                      {ReactHtmlParser(replaceLinebreaks(costDetailsData.not_included_in_price || "-"))}
                    </Col>
                  </Row>
                </section>
              ) : null}
              {costDetailsData?.additional_information ? (
                <section className="pt-4">
                  <Row>
                    <Col className="font-weight-bold font-b3" lg={12} md={12} sm={12}>
                      <FormattedMessage
                        id="quote-request.costDetailAdditionalInfomation"
                        defaultMessage="Additional information"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={12} md={12} sm={12} className="text-secondary font-b3" style={{ whiteSpace: "pre-wrap" }}>
                      {ReactHtmlParser(replaceLinebreaks(costDetailsData.additional_information || "-"))}
                    </Col>
                  </Row>
                </section>
              ) : null}
            </div>
          </>
        )}
        {!canViewQuotePricing && (
          <div
            className="font-weight-bold d-flex justify-content-center align-items-center h-100"
            style={{ padding: "8px 16px", borderRadius: "8px", backgroundColor: "rgba(255, 255, 255, 0.8)" }}
          >
            HIDDEN
          </div>
        )}
      </Col>
      <Col style={{ padding: "8px 0", overflow: "auto" }} md={6} sm={12}>
        <AdditionalInfo
          freightModeType={freightModeType}
          freightMode={service.freight_mode_label}
          quotesData={row.original}
          costDetailsData={costDetailsData}
        />
      </Col>
      {isRefetching && userType === "provider" && <OverlayLoader />}
    </div>
  );
}

SubQuote.propTypes = {
  row: PropTypes.instanceOf(Object).isRequired,
};

export default SubQuote;
